/**
 * The live playhead (0.96.0): a tiny store the sequence viewer writes on every animation frame of playback and the
 * timeline and the mini timeline read through a subscription, so playback does not re-render the whole editor
 * (the pattern of the viewer's graphics clock). While paused the editor's own playhead state writes it too, so
 * `get()` is always the position on screen.
 */
import { useEffect, useState } from 'react';

export interface PlayheadClock {
  get(): number;
  set(ticks: number): void;
  subscribe(fn: (ticks: number) => void): () => void;
}

export function createPlayheadClock(initial = 0): PlayheadClock {
  let value = initial;
  const listeners = new Set<(ticks: number) => void>();
  return {
    get: () => value,
    set(ticks) {
      if (ticks === value) return;
      value = ticks;
      for (const fn of listeners) fn(ticks);
    },
    subscribe(fn) {
      listeners.add(fn);
      return () => { listeners.delete(fn); };
    },
  };
}

/** The playhead as React state, updated at most every `minMs` (for small components that show it). */
export function useClockValue(clock: PlayheadClock, minMs = 33): number {
  const [v, setV] = useState(() => clock.get());
  useEffect(() => {
    let last = 0;
    let timer: number | null = null;
    setV(clock.get());
    const off = clock.subscribe((t) => {
      const now = performance.now();
      if (now - last >= minMs) { last = now; setV(t); return; }
      if (timer === null) timer = window.setTimeout(() => { timer = null; last = performance.now(); setV(clock.get()); }, minMs);
    });
    return () => { off(); if (timer !== null) window.clearTimeout(timer); };
  }, [clock, minMs]);
  return v;
}

/** A store of any value with subscribers (the meter's readings). */
export interface Store<T> { get(): T; set(v: T): void; subscribe(fn: (v: T) => void): () => void }

export function createStore<T>(initial: T): Store<T> {
  let value = initial;
  const listeners = new Set<(v: T) => void>();
  return {
    get: () => value,
    set(v) { value = v; for (const fn of listeners) fn(v); },
    subscribe(fn) { listeners.add(fn); return () => { listeners.delete(fn); }; },
  };
}
